import UserSettingsInBrowserStorage from "../js/UserSettingsInBrowserStorage.js";
import AsyncTaskExecutor from "../js/task-executor/AsyncTaskExecutor.js";
import InjectionRepository from "../js/repository/InjectionRepository.js";
import BatchRepository from "../js/repository/BatchRepository.js";
import SubstanceRepository from "../js/repository/SubstanceRepository.js";
import HttpClient from "../js/repository/HttpClient.js";
import SubstanceDialogCmp from "./SubstanceDialog/SubstanceDialogCmp.js";
import StructureSvgLoader from "../js/repository/StructureSvgLoader.js";
import SubstanceBarCmp from "./SubstanceBar/SubstanceBarCmp.js";
import OrgRepository from "../js/repository/OrgRepository.js";
import UserHeaderCmp from "./page-header/UserHeaderCmp.js";
import GlobalSettingsRepository from "../js/repository/GlobalSettingsRepository.js";
import BatchPageHeaderCmp from "../pages/batch/BatchPageHeader/BatchPageHeaderCmp.js";
import RegressionApiClient from "../js/repository/RegressionApiClient.js";
import PermissionRepository from "../js/repository/PermissionRepository.js";
import TeamRepository from "../js/repository/TeamRepository.js";
import UserRepository from "../js/repository/UserRepository.js";
import {VisualizationDataSelection} from "../js/components/visualization/VisualizationDataSelection.js";
import PlateListCmp from "../js/controller/PlateListCmp.js";
import BatchViewCmp from "./BatchViewCmp/BatchViewCmp.js";
import {VTableComponent} from "../js/components/visualization/table_v2/VTableComponent.js";
import GlobalErrorHandler from "../js/entities/error/GlobalErrorHandler.js";
import PermissionOnResourceErrorHandler from "../js/entities/error/PermissionOnResourceErrorHandler.js";
import OrgBillingQuotaErrorHandler from "../js/entities/error/OrgBillingQuotaErrorHandler.js";

export default class BatchPageIoC {
    /** @type {SecurityContext} */
    #securityContext;

    /**
     * @param {SecurityContext} securityContext
     * @see {HttpClient#createSecurityContext}
     */
    constructor(securityContext) {
        if (!securityContext)
            throw new Error('Security context is required');
        this.#securityContext = securityContext;
        /** @type {Map<String, any>} */
        this._cached = new Map();
        this._userSettings = new UserSettingsInBrowserStorage();
        this._taskExecutor = new AsyncTaskExecutor();
    }

    /**
     * @returns {UserSettingsInBrowserStorage}
     */
    getUserSettings() {
        return this._userSettings;
    }

    /**
     * @returns {AsyncTaskExecutor}
     */
    getTaskExecutor() {
        return this._taskExecutor;
    }
    /**
     * @returns {BatchRepository}
     */
    getBatchRepository() {
        return this._cache('batchRepo', () => new BatchRepository(this.getHttpClient()));
    }
    /**
     * @returns {RegressionApiClient}
     */
    getRegressionApiClient() {
        return this._cache('regressionApiClient', () => new RegressionApiClient(this.getHttpClient()));
    }
    /**
     * @returns {GlobalSettingsRepository}
     */
    getGlobalSettingsRepository() {
        return this._cache('globalSettingsRepo', () => new GlobalSettingsRepository(this.getHttpClient()));
    }

    /** @returns {UserHeaderCmp} */
    getUserHeaderCmp(){
        return this._cache("userHeaderCmp", ()=> new UserHeaderCmp(this.getOrganizationRepository()));
    }

    /** @returns {OrgRepository} */
    getOrganizationRepository() {
        return this._cache('organizationRepo', () => new OrgRepository(this.getHttpClient()));
    }

    /** @return {InjectionRepository} */
    getInjectionRepository() {
        return this._cache('injectionRepo', () => new InjectionRepository(this.getHttpClient()))
    }

    getSubstanceRepository() {
        return this._cache('substanceRepo', () => new SubstanceRepository(this.getHttpClient()));
    }

    /** @returns {PermissionRepository} */
    getPermissionRepository() {
        return this._cache('permissionRepo', () => new PermissionRepository(this.getHttpClient()));
    }

    /** @returns {TeamRepository} */
    getTeamRepository() {
        return this._cache('teamRepo', () => new TeamRepository(this.getHttpClient()));
    }

    /** @returns {UserRepository} */
    getUserRepository() {
        return this._cache('userRepo', () => new UserRepository(this.getHttpClient()));
    }

    /** @return {StructureSvgLoader} */
    getStructureSvgLoader() {
        return this._cache('structSvgLoader', () => new StructureSvgLoader(this.getHttpClient()));
    }

    /** @return {SubstanceDialogCmp} */
    createSubstanceDialog() {
        return new SubstanceDialogCmp(this.getSubstanceRepository(), this.getStructureSvgLoader(),
            this.getInjectionRepository(), this.getUserSettings());
    }

    /** @returns {BatchPageHeaderCmp} */
    getPageHeaderCmp() {
        return this._cache('pageHeaderCmp', () =>
            new BatchPageHeaderCmp(this.getUserHeaderCmp()));
    }
    /**
     * @returns {SubstanceBarCmp}
     */
    getSubstanceBarCmp() {
        return this._cache('substanceBarCmp', () =>
            new SubstanceBarCmp(this.getStructureSvgLoader()));
    }
    /**
     * @returns {HttpClient}
     */
    getHttpClient() {
        return this._cache('httpClient', () => new HttpClient('', this.#securityContext));
    }

    /**
     * @returns {VisualizationDataSelection}
     */
    getVizDataSelection(){
        return this._cache('visDataSelection', () => new VisualizationDataSelection());
    }

    /**
     * @returns {BatchViewCmp}
     */
    getBatchViewCmp(){
        return this._cache('batchViewCmp', () => new BatchViewCmp(
            new PlateListCmp(this.getVizDataSelection()),
            new VTableComponent(this.getVizDataSelection())
        ));
    }

    /**
     * @return {GlobalErrorHandler}
     */
    getGlobalErrorHandler(){
        return this._cache('globalErrorHandler', () => new GlobalErrorHandler(
            new PermissionOnResourceErrorHandler(),
            new OrgBillingQuotaErrorHandler(this.getOrganizationRepository()),
        ));
    }

    _cache(cmpName, createCmpFn) {
        let cmp = this._cached.get(cmpName);
        if(!cmp) {
            cmp = createCmpFn();
            this._cached.set(cmpName, cmp);
        }
        return cmp;
    }
}
